<!-- eslint-disable vue/no-unused-vars -->
<template>
  <div>
    <DatePicker
      v-model="inputValue"
      :mode="mode"
      :isRange="isRange"
      :minuteIncrement="minuteIncrement"
      :minDate="minDate"
      :maxDate="maxDate"
      :disabledDates="disabledDates"
      :isRequired="isRequired"
      is24hr
      @popoverWillShow="opened = true"
      @popoverWillHide="opened = false"
      :locale="locale"
      :popover="{
        placement: 'bottom',
      }"
      :attributes="allAttributes"
      class="date-picker"
    >
      <template
        v-if="mode != 'time'"
        v-slot="{
          inputValue,
          inputEvents,
          isDragging,
          updateValue,
          showPopover,
          hidePopover,
          togglePopover,
        }"
      >
        <div
          v-on="getEventHandlers(inputEvents)"
          class="date_input"
          :class="{
            'date_input--disabled': disabled,
            'date_input--invalid': displayInvalid,
          }"
        >
          <font-awesome-icon
            class="date_input__icon"
            :class="{ 'date_input__icon--disabled': disabled }"
            :icon="['far', 'calendar']"
          />
          <span v-if="forcePlaceholder" class="date_input__text">{{
            placeholder
          }}</span>
          <span
            v-else-if="isRange && inputValue.start"
            class="date_input__text"
          >
            {{ inputValue.start }} - {{ inputValue.end }}
          </span>
          <span v-else-if="!isRange && inputValue" class="date_input__text">
            {{ inputValue }}
          </span>
          <span v-else class="date_input__text">{{ placeholder }}</span>
          <font-awesome-icon
            class="date_input__toggle_icon"
            :icon="['far', dateRangeToggleIcon]"
          />
        </div>
      </template>
    </DatePicker>
    <ValidationMessage
      v-if="displayInvalid"
      :displayInvalid="displayInvalid"
      :validationMessage="externalValidationErrorMessage"
    />
  </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCalendar,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { format } from "date-fns";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import InputBase from "./mixins/InputBase";

library.add(faCalendar, faChevronUp, faChevronDown);

export default {
  name: "DateInput",
  mixins: [InputBase],
  components: {
    FontAwesomeIcon,
    DatePicker,
  },
  props: {
    placeholder: {
      type: String,
      default: "Select date(s)",
    },
    // Used to circumvent an issue where inputValue is not set to null by library, while being passed as value.
    forcePlaceholder: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "date",
      validator(value) {
        const modes = ["date", "dateTime", "time"];

        if (!modes.includes(value)) {
          console.error(
            `[FC error]: DatePicker's "mode" property needs to be either one of these values: ${modes.join(
              ", "
            )}.`
          );
          return false;
        }

        return true;
      },
    },
    isRange: {
      type: Boolean,
      default: false,
    },
    locale: {
      type: String,
      default: "en-GB",
    },
    minDate: {
      type: Date,
      default: null,
    },
    maxDate: {
      type: Date,
      default: null,
    },
    minuteIncrement: {
      type: Number,
      default: 5,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledDates: {
      type: Array,
      default: () => [],
    },
    attributes: {
      type: Array,
      default: () => [],
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    enableExternalValidation: {
      type: Boolean,
    },
    externalValidationError: {
      type: Boolean,
    },
    externalValidationErrorMessage: {
      type: String,
    },
    markToday: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      dragValue: {
        start: null,
        end: null,
      },
      opened: false,
      today: {
        key: "today",
        highlight: {
          style: {
            backgroundColor: "#2E976E",
          },
          contentStyle: {
            color: "#ffffff",
          },
        },
        dates: new Date(),
      },
    };
  },
  computed: {
    displayInvalid() {
      return this.enableExternalValidation && this.externalValidationError;
    },
    displayRange() {
      let displayRange = {
        start: null,
        end: null,
      };
      if (this.mode !== "range") return displayRange;
      if (this.dragValue && this.dragValue.start) {
        displayRange = {
          start: format(this.dragValue.start, "dd/MM/yyyy"),
          end: null,
        };
      } else if (this.inputValue && this.inputValue.start) {
        displayRange = {
          start: format(this.inputValue.start, "dd/MM/yyyy"),
          end: format(this.inputValue.end, "dd/MM/yyyy"),
        };
      }
      return displayRange;
    },
    dateRangeToggleIcon() {
      return this.opened ? "chevron-up" : "chevron-down";
    },
    allAttributes() {
      if (this.markToday) {
        return [...this.attributes, this.today];
      }
      return this.attributes;
    },
  },
  methods: {
    getEventHandlers(events) {
      if (this.disabled) return null;
      if (this.isRange) return events.start;
      return events;
    },
    readArgs(args) {
      console.log(args);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../style_variables/style_variables.scss";

.date_input {
  @extend %body2_style;
  height: 56px;
  border: 1px solid $grey_mischka;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;

  &.date_input--disabled {
    background-color: $grey_bg;
    border: 1px solid $grey_mischka;
    color: $grey_mischka;
  }

  .date_input__text {
    width: 100%;
  }
  .date_input__icon {
    height: 100%;
    margin: 0 16px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    font-size: 16px;
    color: $grey_mid;

    &.date_input__icon--disabled {
      color: $grey_mischka;
    }
  }
  .date_input__toggle_icon {
    margin-right: 16px;
    font-size: 16px;
    color: inherit;
  }

  &.date_input--invalid {
    border: 1px solid $red;
  }
}
.date-picker:deep(.vc-date) {
  display: none;
}
</style>
